/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { request } from '@mairistem/tools';
import { Button, Dimmer, Grid, Icon, Loader } from '@mairistem/composants';
import { Bloc } from '@mairistem/administres-utils';

import { COLORS } from '@/constants';
import { getCurrentTrimestre } from '@/routes';
import { useEntiteStore, useTrimestreStore } from '@/zustand';
import useModalMajStore from '@/zustand/useModalMajStore';
import { useGestionTrimestre } from '@/hooks';
import { GestionTrimestreActions } from '@/enums';
import type { Trimestre } from '@/types';

import ClotureConfirmationModal from './ClotureConfirmationModal';
import ModalGestionTrimestre from './ModalGestionTrimestre/ModalGestionTrimestre';
import ModalEditionListes from './ModalEditionListes';
import ModalWarnTrimestreActif from '../../Common/ModalWarnTrimestreActif';

const BlocGestionTrimestre = (props: { style?: React.CSSProperties }) => {
  const { style } = props;

  const blocked = useEntiteStore((state) => state.blocked);
  const setTrimestre = useTrimestreStore((state) => state.setTrimestre);
  const isCurrentTrimestreActif = useTrimestreStore(
    (state) => state.isCurrentTrimestreActif,
  );
  const shouldWarnUser = useTrimestreStore((state) => state.shouldWarnUser);
  const setShouldWarnUser = useTrimestreStore(
    (state) => state.setShouldWarnUser,
  );
  const isModalMajOpen = useModalMajStore((state) => state.open);

  const {
    states: {
      trimestre,
      loading,
      dateCloture,
      numerotation,
      generation,
      edition,
      telechargement,
      modalOpen,
      confirmationModalOpen,
      editionModalOpen,
    },
    actions: {
      setLoading,
      initializeTrimestre,
      handleNumerotation,
      handleGeneration,
      handleTelechargement,
      handleEdition,
      handleCloture,
      handleModalCloture,
      handleGestionTrimestreModalOpen,
      handleGestionTrimestreModalClose,
      handleEditionModalOpen,
      handleEditionModalClose,
      setConfirmationModalOpen,
    },
  } = useGestionTrimestre();

  console.log('telechargement: ', telechargement);
  const { periode, annee } = trimestre || {};

  const rowStyle = {
    backgroundColor: COLORS.GreyDashboardColor,
    borderRadius: '16px',
    padding: 10,
    marginBottom: '0.5em',
    alignItems: 'center',
  };

  const iconStyle = {
    backgroundColor: 'white',
    padding: '3px',
    borderRadius: '100%',
    marginRight: '8px',
  };

  const buttonStyle = {
    width: 140,
    alignItems: 'center',
    fontSize: '0.8em',
    display: 'flex',
    justifySelf: 'right',
    justifyContent: 'center',
  };

  const fetchCurrentTrimestre = React.useCallback(() => {
    setLoading(true);

    return request
      .get(getCurrentTrimestre())
      .then((data: Trimestre) => {
        setTrimestre(data);
        initializeTrimestre(data);
      })
      .catch(() => {
        toast('Une erreur est survenue lors de la récupération des données', {
          type: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setLoading, initializeTrimestre, setTrimestre]);

  React.useEffect(() => {
    if (!blocked) {
      fetchCurrentTrimestre();
    }
  }, [blocked, fetchCurrentTrimestre]);

  const handleClotureAndRefetch = React.useCallback(async () => {
    const success = await handleCloture();
    if (success) {
      await fetchCurrentTrimestre();
    }
  }, [handleCloture, fetchCurrentTrimestre]);

  const renderConfirmCloture = React.useMemo(
    () => (
      <ClotureConfirmationModal
        loading={loading}
        isClotured={!_.isNil(dateCloture)}
        onClose={() => setConfirmationModalOpen(false)}
        onConfirm={handleClotureAndRefetch}
      />
    ),
    [loading, dateCloture, handleClotureAndRefetch, setConfirmationModalOpen],
  );

  return (
    <Bloc
      icon="calendar"
      header={`Gestion du trimestre en cours ${!_.isNil(periode) && !_.isNil(annee) ? `- T${periode} ${annee}` : ''}`}
      style={style}
    >
      <Grid
        className="bloc-gestion-trimestre"
        style={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          padding: '1em',
        }}
      >
        <Grid.Row style={rowStyle}>
          <Grid.Column width={11} verticalAlign="middle">
            <b>Numérotation des recensés</b>
            <p style={{ fontStyle: 'italic' }}>
              Numérotation selon l&apos;ordre alphabétique des recensés
            </p>
          </Grid.Column>
          <Grid.Column width={5} textAlign="center">
            <Button
              data-testid="gestion-trimestre-bouton-numerotation"
              color={_.isNil(numerotation) ? 'blue' : 'green'}
              content={!_.isNil(numerotation) ? 'Dénuméroter' : 'Numéroter'}
              disabled={!_.isNil(dateCloture)}
              className="custom-blue-button"
              onClick={() =>
                handleNumerotation(
                  !_.isNil(numerotation)
                    ? GestionTrimestreActions.DENUMEROTATION
                    : GestionTrimestreActions.NUMEROTATION,
                )
              }
              style={buttonStyle}
            >
              <Icon name="FileDigit" iconSet="Lucide" style={iconStyle} />
              <span style={{ flex: 1, textAlign: 'center' }}>
                {!_.isNil(numerotation) ? 'Dénuméroter' : 'Numéroter'}
              </span>
            </Button>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row style={rowStyle}>
          <Grid.Column width={11} verticalAlign="middle">
            <b>Edition des listes de recensement</b>
            <p style={{ fontStyle: 'italic' }}>
              Liste des jeunes recensés de 16 à 25 ans
            </p>
          </Grid.Column>
          <Grid.Column width={5} textAlign="center">
            <Button
              data-testid="gestion-trimestre-bouton-visualiser"
              content="Visualiser"
              {...(!_.isNil(numerotation) && { color: 'green' })}
              disabled={_.isNil(numerotation)}
              onClick={handleEditionModalOpen}
              style={buttonStyle}
            >
              <Icon name="Eye" iconSet="Lucide" style={iconStyle} />
              <span style={{ flex: 1, textAlign: 'center' }}>Visualiser</span>
            </Button>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row style={rowStyle}>
          <Grid.Column width={11} verticalAlign="middle">
            <b>Génération du fichier Pecoto+</b>
            <p style={{ fontStyle: 'italic' }}>
              Génération du fichier au format v1.0.1 du 02/09/2020
            </p>
          </Grid.Column>
          <Grid.Column width={5} textAlign="center">
            <Button
              data-testid="gestion-trimestre-bouton-generation"
              content=""
              {...(!_.isNil(numerotation) &&
                _.isNil(generation) && { color: 'blue' })}
              disabled={
                !_.isNil(dateCloture) ||
                _.isNil(numerotation) ||
                !_.isNil(generation)
              }
              onClick={handleGeneration}
              style={buttonStyle}
            >
              <Icon name="Loader" iconSet="Lucide" style={iconStyle} />
              <span style={{ flex: 1, textAlign: 'center' }}>Générer</span>
            </Button>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row style={rowStyle}>
          <Grid.Column width={11} verticalAlign="middle">
            <b>Téléchargement du fichier Pecoto+</b>
            <p style={{ fontStyle: 'italic' }}>
              A passer dans Pecoto Helper avant envoi à votre BSN/CSN
            </p>
          </Grid.Column>
          <Grid.Column width={5} textAlign="center">
            <Button
              data-testid="gestion-trimestre-bouton-telecharger"
              content=""
              {...(!_.isNil(generation) &&
                !_.isNil(numerotation) && { color: 'blue' })}
              disabled={_.isNil(numerotation) || _.isNil(generation)}
              onClick={handleTelechargement}
              style={buttonStyle}
            >
              <Icon name="ArrowDownToLine" iconSet="Lucide" style={iconStyle} />
              <span style={{ flex: 1, textAlign: 'center' }}>Télécharger</span>
            </Button>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row style={rowStyle}>
          <Grid.Column width={11} verticalAlign="middle">
            <b>Ouvrir le site Presage - Espace Mairie</b>
            <p style={{ fontStyle: 'italic' }}>Dépot du fichier PECOTO+</p>
          </Grid.Column>
          <Grid.Column width={5} textAlign="center">
            <Button
              data-testid="gestion-trimestre-bouton-presage"
              content=""
              {...(!_.isNil(generation) &&
                !_.isNil(numerotation) &&
                telechargement && { color: 'blue' })}
              disabled={
                _.isNil(numerotation) || _.isNil(generation) || !telechargement
              }
              onClick={() =>
                window.open(
                  'https://presaje.sga.defense.gouv.fr/mairie/connexion',
                  '_blank',
                )
              }
              style={buttonStyle}
            >
              <Icon name="FileOutput" iconSet="Lucide" style={iconStyle} />
              <span style={{ flex: 1, textAlign: 'center' }}>Télédéposer</span>
            </Button>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row style={rowStyle}>
          <Grid.Column width={11}>
            <b>Clôture du trimestre</b>
            <p style={{ fontStyle: 'italic' }}>
              Clôture du trimestre et ouverture du prochain
            </p>
          </Grid.Column>
          <Grid.Column width={5}>
            <Button
              data-testid="gestion-trimestre-bouton-cloture"
              content="Clôturer"
              {...(!_.isNil(generation) &&
                !_.isNil(numerotation) && { color: 'red' })}
              disabled={
                _.isNil(numerotation) ||
                !_.isNil(dateCloture) ||
                _.isNil(generation) ||
                !telechargement
              }
              onClick={handleModalCloture}
              style={buttonStyle}
            >
              <Icon name="X" iconSet="Lucide" style={iconStyle} />
              <span style={{ flex: 1, textAlign: 'center' }}>Clôturer</span>
            </Button>
            {confirmationModalOpen && renderConfirmCloture}
          </Grid.Column>
        </Grid.Row>

        <Grid.Row style={{ paddingTop: '1em', paddingBottom: 0 }}>
          <Grid.Column
            textAlign="right"
            style={{ cursor: 'pointer' }}
            data-testid="gestion-trimestre-modal-voir-tout"
            onClick={handleGestionTrimestreModalOpen}
          >
            Voir tout
            <Icon
              name="arrow right"
              style={{
                border: '1px solid grey',
                padding: '0.7em',
                borderRadius: '4px',
                display: 'inline',
                marginLeft: '15px',
              }}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {modalOpen && (
        <ModalGestionTrimestre
          trimestre={periode}
          annee={annee}
          numerotation={numerotation}
          generation={generation}
          edition={edition}
          dateCloture={dateCloture}
          telechargement={telechargement}
          loading={loading}
          onNumerotation={handleNumerotation}
          onGeneration={handleGeneration}
          onTelechargement={handleTelechargement}
          onEdition={handleEdition}
          onCloture={handleModalCloture}
          onClose={handleGestionTrimestreModalClose}
        />
      )}
      {editionModalOpen && (
        <ModalEditionListes
          edition={edition}
          loading={loading}
          isClotured={!_.isNil(dateCloture)}
          onEdition={handleEdition}
          onClose={handleEditionModalClose}
        />
      )}
      {!isCurrentTrimestreActif && shouldWarnUser && !isModalMajOpen && (
        <ModalWarnTrimestreActif
          onClose={() => {
            setShouldWarnUser(false);
          }}
        />
      )}

      {loading && (
        <Dimmer active inverted>
          <Loader />
        </Dimmer>
      )}
    </Bloc>
  );
};

export default BlocGestionTrimestre;
